/**
 * Utilidades y constantes específicas para el modelo Persona.
 * 
 * Este archivo contiene:
 * - Constantes de longitud máxima según BD para campos de Persona
 * - Mensajes de validación específicos de Persona
 * - Funciones helper específicas de Persona
 * 
 * Las utilidades genéricas de input/validación/formato están en:
 * - input-utils.ts (regex, handlers keypress/input)
 * - validation-utils.ts (funciones de validación)
 * - format-utils.ts (normalización, capitalización, limpieza)
 * 
 * @author Emerson Rodriguez - Ingeneo
 */

import { Persona } from '../domain/model/persona/persona';
import { SEXO_DISPLAY } from './constants-ejb';

// ============================================
// Constantes de longitud máxima según BD Oracle
// Tabla: TPSC_PERSONAS
// ============================================

export const PERSONA_MAX_LENGTHS = {
  /** CDTIPO_IDENTIFICACION - VARCHAR2(2) */
  tipoId: 2,
  cdtipo_identificacion: 2,
  /** NMIDENTIFICACION - VARCHAR2(20) */
  numId: 20,
  nmidentificacion: 20,
  /** DSNOMBRE1 - VARCHAR2(50) */
  nombre1: 50,
  dsnombre1: 50,
  /** DSNOMBRE2 - VARCHAR2(50) */
  nombre2: 50,
  dsnombre2: 50,
  /** DSAPELLIDO1 - VARCHAR2(50) */
  apellido1: 50,
  dsapellido1: 50,
  /** DSAPELLIDO2 - VARCHAR2(50) */
  apellido2: 50,
  dsapellido2: 50,
  /** DSEMAIL - VARCHAR2(100) */
  email: 100,
  dsemail: 100,
  /** CDDIGITO_VERIFICACION - VARCHAR2(1) */
  digitoVerificacion: 1,
  cddigito_verificacion: 1,
  /** DSNOMBRE_COMERCIAL - VARCHAR2(200) */
  nombreComercial: 200,
  dsnombre_comercial: 200,
  /** DSNOMBRE_LEGAL - VARCHAR2(200) */
  nombreLegal: 200,
  dsnombre_legal: 200
} as const;

// ============================================
// Mensajes de validación para Persona
// ============================================

export const PERSONA_VALIDATION_MESSAGES = {
  nombre: {
    required: 'El nombre es requerido',
    pattern: 'Solo se permiten letras y espacios',
    maxLength: `Máximo ${PERSONA_MAX_LENGTHS.nombre1} caracteres`
  },
  apellido: {
    required: 'El apellido es requerido',
    pattern: 'Solo se permiten letras y espacios',
    maxLength: `Máximo ${PERSONA_MAX_LENGTHS.apellido1} caracteres`
  },
  identificacion: {
    required: 'El número de identificación es requerido',
    pattern: 'Solo se permiten números, guiones y puntos',
    maxLength: `Máximo ${PERSONA_MAX_LENGTHS.numId} caracteres`
  },
  tipoIdentificacion: {
    required: 'El tipo de identificación es requerido'
  },
  email: {
    pattern: 'Formato de email inválido',
    maxLength: `Máximo ${PERSONA_MAX_LENGTHS.email} caracteres`
  },
  nombreComercial: {
    maxLength: `Máximo ${PERSONA_MAX_LENGTHS.nombreComercial} caracteres`
  },
  nombreLegal: {
    maxLength: `Máximo ${PERSONA_MAX_LENGTHS.nombreLegal} caracteres`
  },
  required: 'Este campo es requerido'
};

// ============================================
// Helper functions específicas de Persona
// ============================================

/**
 * Obtiene el nombre completo formateado
 */
export function getNombreCompleto(persona: Persona): string {
  const parts = [
    persona.dsnombre1,
    persona.dsnombre2,
    persona.dsapellido1,
    persona.dsapellido2
  ].filter(p => p && p.trim() !== '');
  return parts.join(' ');
}

/**
 * Verifica si la persona está activa
 */
export function isActivo(persona: Persona): boolean {
  return persona.snactivo === 'S';
}

/**
 * Obtiene el display del tipo de identificación
 */
export function getTipoIdDisplay(tipoId: string | null | undefined): string {
  const tipos: Record<string, string> = {
    'C': 'Cédula de Ciudadanía',
    'E': 'Cédula de Extranjería',
    'N': 'NIT',
    'P': 'Pasaporte',
    'T': 'Tarjeta de Identidad',
    'R': 'Registro Civil'
  };
  return tipoId ? (tipos[tipoId] || tipoId) : '';
}

/**
 * Obtiene el display del sexo.
 * Usa constantes centralizadas de constants-ejb.ts
 */
export function getSexoDisplay(sexo: string | null | undefined): string {
  return sexo ? (SEXO_DISPLAY[sexo] || sexo) : '';
}

/**
 * Valida si la persona tiene los campos mínimos requeridos
 */
export function isPersonaValid(persona: Partial<Persona>): boolean {
  return !!(
    persona.cdtipo_identificacion &&
    persona.nmidentificacion &&
    persona.dsnombre1 &&
    persona.dsapellido1
  );
}
